import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHaptics } from './hooks';
import FeatureGate from './FeatureGate';

/**
 * MobileControlBar - 2025 Thumb-Zone Control Bar
 *
 * Bottom-positioned control bar optimized for one-handed mobile use.
 * Follows iOS FaceTime, Google Meet, and Discord design patterns.
 *
 * Features:
 * - Safe area inset support (notch devices)
 * - Large touch targets (56px)
 * - Haptic feedback on all interactions
 * - Auto-hide capability
 * - Dark theme default
 */
const MobileControlBar = ({
  // Control states
  isMuted = false,
  isCameraOn = false,
  isScreenSharing = false,
  friendCount = 0,

  // Event handlers
  onMuteToggle,
  onCameraToggle,
  onScreenShareToggle,
  onInviteFriend,
  onEndSession,

  // UI state
  isVisible = true,
  showCameraButton = true,
  showScreenShareButton = true,
  showInviteButton = true,

  // Feature access (for feature gating with paywall)
  canScreenShare = true,
  canInviteFriends = true,
  user = null,
  onShowToast = null,
}) => {
  const haptics = useHaptics();

  const handleMuteToggle = () => {
    haptics.medium();
    onMuteToggle?.();
  };

  const handleCameraToggle = () => {
    haptics.medium();
    onCameraToggle?.();
  };

  const handleScreenShareToggle = () => {
    haptics.medium();
    onScreenShareToggle?.();
  };

  const handleInviteFriend = () => {
    haptics.light();
    onInviteFriend?.();
  };

  const handleEndSession = () => {
    haptics.sessionEnd();
    onEndSession?.();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          style={styles.container}
        >
          {/* Glassmorphic background */}
          <div style={styles.background} />

          {/* Control buttons grid */}
          <div style={styles.controlGrid}>
            {/* Mute Button - Primary */}
            <ControlButton
              icon={isMuted ? <MicOffIcon /> : <MicOnIcon />}
              label={isMuted ? 'Unmute' : 'Mute'}
              isActive={!isMuted}
              activeColor="#30d158"
              inactiveColor="#ff453a"
              onClick={handleMuteToggle}
            />

            {/* Camera Button */}
            {showCameraButton && (
              <ControlButton
                icon={isCameraOn ? <CameraOnIcon /> : <CameraOffIcon />}
                label="Camera"
                isActive={isCameraOn}
                activeColor="#30d158"
                onClick={handleCameraToggle}
              />
            )}

            {/* Screen Share Button - Feature Gated with Paywall */}
            {showScreenShareButton && (
              <FeatureGate
                feature="SCREEN_SHARING"
                user={user}
                mode="inline"
                showUpgradeMessage={true}
                onShowToast={onShowToast}
              >
                <ControlButton
                  icon={<ScreenShareIcon />}
                  label="Share"
                  isActive={isScreenSharing}
                  activeColor="#bf5af2"
                  onClick={handleScreenShareToggle}
                />
              </FeatureGate>
            )}

            {/* Invite Friend Button - Feature Gated with Paywall */}
            {showInviteButton && (
              <FeatureGate
                feature="FRIEND_TRAINING_HOST"
                user={user}
                mode="inline"
                showUpgradeMessage={true}
                onShowToast={onShowToast}
              >
                <ControlButton
                  icon={<AddFriendIcon />}
                  label={friendCount > 0 ? `+${friendCount}` : 'Invite'}
                  isActive={false}
                  badge={friendCount > 0 ? friendCount : null}
                  onClick={handleInviteFriend}
                />
              </FeatureGate>
            )}

            {/* End Session Button */}
            <ControlButton
              icon={<EndCallIcon />}
              label="End"
              isDestructive={true}
              onClick={handleEndSession}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Individual Control Button Component
 */
const ControlButton = ({
  icon,
  label,
  isActive = false,
  isDestructive = false,
  activeColor = '#30d158',
  inactiveColor = 'rgba(255, 255, 255, 0.15)',
  badge = null,
  disabled = false,
  onClick,
}) => {
  const haptics = useHaptics();

  const handleClick = () => {
    if (disabled) {
      haptics.warning();
      return;
    }
    onClick?.();
  };

  const getBackgroundColor = () => {
    if (isDestructive) return '#ff453a';
    if (disabled) return 'rgba(255, 255, 255, 0.08)';
    if (isActive) return activeColor;
    return inactiveColor;
  };

  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.05 }}
      whileTap={{ scale: disabled ? 1 : 0.92 }}
      onClick={handleClick}
      style={{
        ...styles.controlButton,
        background: getBackgroundColor(),
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {/* Icon */}
      <div style={styles.iconWrapper}>
        {icon}
      </div>

      {/* Label */}
      <span style={styles.buttonLabel}>{label}</span>

      {/* Badge (friend count) */}
      {badge && (
        <div style={styles.badge}>
          {badge}
        </div>
      )}
    </motion.button>
  );
};

// ============================================
// SVG Icons (Inline for performance)
// ============================================

const MicOnIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const MicOffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const CameraOnIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const CameraOffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56" />
  </svg>
);

const ScreenShareIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const AddFriendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const EndCallIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
  </svg>
);

// ============================================
// Styles
// ============================================

const styles = {
  container: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1100, // Higher z-index to ensure visibility above footer
    // Safe area support
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },

  background: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(28, 28, 30, 0.95)', // Slightly more opaque for better visibility
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },

  controlGrid: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center', // Center the controls
    alignItems: 'center',
    padding: '16px 20px 20px 20px', // More padding for breathing room
    maxWidth: '400px', // Narrower for cleaner look
    margin: '0 auto',
    gap: '12px', // More gap between buttons
  },

  controlButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    width: '56px', // Slightly smaller for better fit
    height: '64px',
    border: 'none',
    borderRadius: '16px',
    color: 'white',
    position: 'relative',
    transition: 'background 0.2s ease, transform 0.1s ease',
    flexShrink: 0, // Prevent shrinking
  },

  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
  },

  buttonLabel: {
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    opacity: 0.85,
  },

  badge: {
    position: 'absolute',
    top: '2px',
    right: '4px',
    background: '#30d158', // Green for positive (friends online)
    color: 'white',
    fontSize: '9px',
    fontWeight: 700,
    padding: '2px 5px',
    borderRadius: '8px',
    minWidth: '16px',
    textAlign: 'center',
  },
};

export default MobileControlBar;
